import { createContext, useContext, useMemo, type ReactNode } from "react";

// STUB — CHG-1167 scaffold. Real Intl provider with org-locale wiring lands
// ε CHG-1163 (integrations workspace). Per the v1 Rule 6/10 lineage all
// formatting MUST go through useFormatters() — page code never calls Intl.* directly.
//
// Bootstrap-layer exception: this file IS the bootstrap layer; Intl.* here
// is permitted (mirrors v1's IntlProvider exemption documented in CLAUDE.md §5c Rule 6).

export interface Formatters {
  formatCurrency: (cents: number, currency?: string) => string;
  formatDate: (iso: string, style?: "short" | "medium" | "long" | "relative") => string;
  formatDateTime: (iso: string) => string;
  formatNumber: (n: number) => string;
  formatPercent: (n: number, decimals?: number) => string;
  timezone: string;
}

const browserTimezone =
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";

const fallbackFormatters: Formatters = {
  formatCurrency: (cents, currency = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format((cents ?? 0) / 100),
  formatDate: (iso, style = "medium") => {
    if (!iso) return "";
    const d = new Date(iso);
    if (style === "relative") {
      const diffMs = Date.now() - d.getTime();
      const diffDays = Math.floor(diffMs / 86400000);
      if (Math.abs(diffDays) < 1) return "today";
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(-diffDays, "day");
    }
    return new Intl.DateTimeFormat("en-US", { dateStyle: style }).format(d);
  },
  formatDateTime: (iso) =>
    new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    ),
  formatNumber: (n) => new Intl.NumberFormat("en-US").format(n),
  formatPercent: (n, decimals = 0) =>
    new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: decimals,
    }).format(n),
  timezone: browserTimezone,
};

const IntlContext = createContext<Formatters>(fallbackFormatters);

export function IntlProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => fallbackFormatters, []);
  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}

export function useFormatters(): Formatters {
  return useContext(IntlContext);
}
